/**
 * Client-safe Pro Clubs types and pure helpers.
 * Import from here in client components — never from proclubs-client.ts (server-only).
 */

export type Platform = 'common-gen5' | 'common-gen4' | 'ps5' | 'xbsx' | 'pc' | 'ps4' | 'xone' | (string & {})

export type MatchType = 'friendlyMatch' | 'leagueMatch' | 'playoffMatch' | (string & {})

export interface ProclubsClientOptions {
    baseUrl?: string
    fetchImpl?: typeof fetch
    headers?: Record<string, string>
    referer?: string
    timeoutMs?: number
    cookies?: string
    autoFetchCookies?: boolean
    clientId?: string
    proxyUrl?: string
}

export interface GetMatchesParams {
    clubIds: number | string | Array<number | string>
    platform?: Platform
    matchType?: MatchType
}

export interface EaMatchResponse extends Array<EaMatch> {}

export interface EaMatch {
    matchId: string
    timestamp: number
    timeAgo?: { number: number; unit: string }
    clubs: Record<string, EaClubMatch>
    players?: Record<string, Record<string, EaPlayerStats>>
    aggregate?: Record<string, EaAggregateStats>
}

export interface EaClubMatch {
    date: string
    gameNumber: string
    goals: string
    goalsAgainst: string
    losses: string
    matchType: string
    result: string
    score: string
    season_id: string
    TEAM: string
    ties: string
    winnerByDnf: string
    wins: string
    details?: EaClubDetails
}

export interface EaClubDetails {
    name: string
    clubId: number
    regionId?: number
    teamId?: number
    customKit?: Record<string, unknown>
}

export interface EaPlayerStats {
    playername: string
    pos?: string
    rating?: string
    goals?: string
    assists?: string
    shots?: string
    passesmade?: string
    passattempts?: string
    tacklesmade?: string
    tackleattempts?: string
    saves?: string
    goalsconceded?: string
    mom?: string
    secondsPlayed?: string
    userResult?: string
    [k: string]: unknown
}

export interface EaAggregateStats {
    goals: number
    assists: number
    shots: number
    passesmade: number
    passattempts: number
    tacklesmade: number
    tackleattempts: number
    saves: number
    goalsconceded: number
    cleansheetsdef: number
    cleansheetsgk: number
    mom: number
    secondsPlayed: number
    rating: number
    [k: string]: unknown
}

export interface NormalizedMatch {
    matchId: string
    timestamp: number
    playedAt: Date
    clubId: string
    clubName?: string
    opponentClubId: string
    opponentName?: string
    goalsFor: number
    goalsAgainst: number
    isWin: boolean
    isDraw: boolean
    isLoss: boolean
    raw: {
        match: EaMatch
        club: EaClubMatch
        opponent: EaClubMatch
    }
}

export interface RecordSummary {
    played: number
    wins: number
    draws: number
    losses: number
    goalsFor: number
    goalsAgainst: number
    goalDiff: number
}

export interface ClubSearchResponse {
    clubs?: Array<{
        clubId: number
        clubName: string
        platform?: string
        [k: string]: unknown
    }>
    [k: string]: unknown
}

export interface MemberStatsResponse {
    members: EaMemberStats[]
    positionCount?: {
        midfielder?: number
        goalkeeper?: number
        forward?: number
        defender?: number
        [k: string]: number | undefined
    }
    [k: string]: unknown
}

export interface EaMemberStats {
    name: string
    gamesPlayed: string
    winRate: string
    goals: string
    assists: string
    cleanSheetsDef: string
    cleanSheetsGK: string
    shotSuccessRate: string
    passesMade: string
    passSuccessRate: string
    ratingAve: string
    tacklesMade: string
    tackleSuccessRate: string
    proName: string
    proPos: string
    proStyle: string
    proHeight: string
    proNationality: string
    proOverall: string
    proOverallStr: string
    manOfTheMatch: string
    redCards: string
    prevGoals: string
    prevGoals1: string
    prevGoals2: string
    prevGoals3: string
    prevGoals4: string
    prevGoals5: string
    prevGoals6: string
    prevGoals7: string
    prevGoals8: string
    prevGoals9: string
    prevGoals10: string
    favoritePosition: string
    [k: string]: unknown
}

export function normalizeClubIds(input: GetMatchesParams['clubIds']): string {
    if (Array.isArray(input)) return input.map(String).join(',')
    return String(input)
}

function toIntSafe(v: unknown): number {
    const n = typeof v === 'number' ? v : parseInt(String(v ?? '0'), 10)
    return Number.isFinite(n) ? n : 0
}

export function normalizeForClubId(matches: EaMatchResponse, clubId: string): NormalizedMatch[] {
    const out: NormalizedMatch[] = []

    for (const m of matches) {
        if (!m?.clubs || typeof m.clubs !== 'object') continue

        const club = m.clubs[clubId]
        if (!club) continue

        const clubKeys = Object.keys(m.clubs)
        const opponentClubId = clubKeys.find(k => k !== clubId) ?? ''
        const opponent = opponentClubId ? m.clubs[opponentClubId] : undefined
        if (!opponent || !opponentClubId) continue

        const gf = toIntSafe(club.goals)
        const ga = toIntSafe(club.goalsAgainst)
        const playedAt = new Date((m.timestamp ?? toIntSafe(club.date)) * 1000)

        const isWin = gf > ga
        const isDraw = gf === ga
        const isLoss = gf < ga

        out.push({
            matchId: String(m.matchId),
            timestamp: Number(m.timestamp),
            playedAt,
            clubId,
            clubName: club.details?.name,
            opponentClubId,
            opponentName: opponent.details?.name,
            goalsFor: gf,
            goalsAgainst: ga,
            isWin,
            isDraw,
            isLoss,
            raw: { match: m, club, opponent }
        })
    }

    out.sort((a, b) => b.timestamp - a.timestamp)
    return out
}

export function summarizeRecord(matches: NormalizedMatch[]): RecordSummary {
    let wins = 0
    let draws = 0
    let losses = 0
    let gf = 0
    let ga = 0

    for (const m of matches) {
        gf += m.goalsFor
        ga += m.goalsAgainst
        if (m.isWin) wins++
        else if (m.isDraw) draws++
        else losses++
    }

    return {
        played: matches.length,
        wins,
        draws,
        losses,
        goalsFor: gf,
        goalsAgainst: ga,
        goalDiff: gf - ga
    }
}

export function buildFriendliesUrl(params: GetMatchesParams, baseUrl = 'https://proclubs.ea.com'): string {
    const platform = params.platform ?? 'common-gen5'
    const matchType = params.matchType ?? 'friendlyMatch'
    const clubIds = normalizeClubIds(params.clubIds)

    const url = new URL(`${baseUrl.replace(/\/+$/, '')}/api/fc/clubs/matches`)
    url.searchParams.set('matchType', matchType)
    url.searchParams.set('platform', platform)
    url.searchParams.set('clubIds', clubIds)
    return url.toString()
}

export function detectExtraTimeAndPenalties(
    match: NormalizedMatch,
    rawMatch: EaMatch | null
): { hasExtraTime: boolean; wonOnPenalties: boolean } {
    if (!rawMatch || !rawMatch.players) {
        return { hasExtraTime: false, wonOnPenalties: false }
    }

    const NINETY_MINUTES_IN_SECONDS = 90 * 60
    let hasExtraTime = false
    let totalPlayerGoals = 0

    for (const clubId in rawMatch.players) {
        const clubPlayers = rawMatch.players[clubId]
        if (!clubPlayers) continue

        for (const playerId in clubPlayers) {
            const player = clubPlayers[playerId]
            const secondsPlayed = toIntSafe(player.secondsPlayed || (player as { gameTime?: string }).gameTime)
            if (secondsPlayed > NINETY_MINUTES_IN_SECONDS) {
                hasExtraTime = true
            }
            totalPlayerGoals += toIntSafe(player.goals)
        }
    }

    const totalFinalScore = match.goalsFor + match.goalsAgainst
    const hasGoalDifference = match.goalsFor !== match.goalsAgainst
    const wonOnPenalties = hasGoalDifference && totalPlayerGoals === 0 && totalFinalScore > 0

    return { hasExtraTime, wonOnPenalties }
}
